/**
 * Modul "Lagersystem": eigenständiger Bereich für die Discord-
 * Lagersynchronisierung - bewusst getrennt vom bestehenden "Lager"-Modul.
 * Sieben Bereiche: Übersicht (Bestand aller drei Lager), Einlagerungen,
 * Auslagerungen (jeweils "wer hat was gebucht"), Bestandskorrektur
 * (einmalige Startbestand-Erfassung), Lager-Zuordnung, Sync-Protokoll
 * (vollständiges Protokoll inkl. nicht zuordenbarer Meldungen) sowie
 * Einstellungen (Status, Discord-Log-Channel). Die eigentliche
 * Bestandsänderung läuft unsichtbar im Hintergrund (Cron Trigger, siehe
 * backend/src/scheduled.ts) und wirkt auf denselben Lagerbestand, der im
 * "Lager"-Modul angezeigt wird.
 *
 * Kein eigener Reiter für "Artikel-Zuordnung" (bewusst entfernt) - neue
 * Item-Zuordnungen entstehen weiterhin direkt über den "Ja"-Fluss bei nicht
 * zuordenbaren Meldungen im Reiter "Übersicht" (siehe UnmappedItemRow unten),
 * der API-Endpunkt /api/warehouse-sync/item-mappings bleibt dafür bestehen.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card, CardHeader } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { StatCard } from "../../components/ui/StatCard";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { formatDateTime } from "../../utils/formatDate";
import type {
  DiscordStorageMapping, DiscordSyncEvent, DiscordSyncStatus,
  PaginatedResult, Warehouse, WarehouseStockOverview,
} from "../../types";

type Tab = "overview" | "inbound" | "outbound" | "baseline" | "storage" | "log" | "settings";

const EVENT_STATUS_LABELS: Record<DiscordSyncEvent["status"], string> = {
  applied: "Gebucht", unmapped_item: "Unbekanntes Item", unmapped_storage: "Unbekanntes Lager", ignored: "Ignoriert", error: "Fehler",
};
const EVENT_STATUS_TONE: Record<DiscordSyncEvent["status"], "success" | "warning" | "danger" | "gray"> = {
  applied: "success", unmapped_item: "warning", unmapped_storage: "warning", ignored: "gray", error: "danger",
};

export default function WarehouseSync() {
  const [tab, setTab] = useState<Tab>("overview");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Lagersystem</h1>
        <p className="text-sm text-gray-500 mt-1">
          Automatische Synchronisierung des Lagerbestands mit den Ein-/Auslagerungs-Meldungen aus Discord.
        </p>
      </div>

      <div className="flex gap-2 border-b border-surface-border overflow-x-auto">
        {([
          ["overview", "Übersicht"],
          ["inbound", "Einlagerungen"],
          ["outbound", "Auslagerungen"],
          ["baseline", "Bestandskorrektur"],
          ["storage", "Lager-Zuordnung"],
          ["log", "Sync-Protokoll"],
          ["settings", "Einstellungen"],
        ] as [Tab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === key ? "border-accent text-accent" : "border-transparent text-gray-500 hover:text-gray-300"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <StockOverviewTab onGoToTab={setTab} />}
      {tab === "inbound" && <DirectionLogTab direction="in" title="Einlagerungen" emptyMessage="Noch keine Einlagerungen synchronisiert." />}
      {tab === "outbound" && <DirectionLogTab direction="out" title="Auslagerungen" emptyMessage="Noch keine Auslagerungen synchronisiert." />}
      {tab === "baseline" && <StockBaselineTab />}
      {tab === "storage" && <StorageMappingTab />}
      {tab === "log" && <SyncLogTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Übersicht: alle drei Lager mit ihren Artikeln und Mengen
// ----------------------------------------------------------------------------
// sessionStorage statt localStorage: Erinnerung soll erst nach Schließen und
// erneutem Öffnen des Browsers (neue Browser-Sitzung) wieder erscheinen, aber
// innerhalb derselben Sitzung (Tab-Wechsel, Neuladen der Seite) verschwunden
// bleiben, sobald einmal erfolgreich synchronisiert wurde.
const SYNC_REMINDER_KEY = "lbv_warehouse_sync_reminder_dismissed";

function hasSyncedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SYNC_REMINDER_KEY) === "1";
  } catch {
    return false;
  }
}

function markSyncedThisSession(): void {
  try {
    sessionStorage.setItem(SYNC_REMINDER_KEY, "1");
  } catch {
    // z.B. Privater Modus ohne Storage-Zugriff - Erinnerung bleibt dann
    // dauerhaft sichtbar, aber ohne Funktionseinschränkung.
  }
}

// Ein "unbekannter Artikel" (status "unmapped_item") ist bereits korrekt
// einem Lager zugeordnet (der Discord-Lagerschlüssel wurde erfolgreich
// aufgelöst - nur der Item-Name selbst kennt noch keinen LBV-Artikel).
// GET /unmapped-items liefert bereits nach Item-Name+Lager gruppiert und
// bewusst nur die AKTUELL noch unbekannten (nicht die eingefrorene
// Historie) - sobald eine Zuordnung existiert, verschwindet der Eintrag von
// selbst, egal ob sie über "Ja" hier oder über Artikel-Zuordnung entstand.
interface UnmappedItemGroup {
  item_label: string;
  warehouse_id: number;
  warehouse_name: string;
  quantity: number;
  occurrences: number;
}

function groupKey(g: UnmappedItemGroup): string {
  return `${g.item_label}|${g.warehouse_id}`;
}

// "Doppelte suchen" (Recht "discord_sync.duplicates", ab Werk nur President) -
// Ergebnis des Endpunkts GET /warehouses/:id/duplicates.
interface DuplicateItem {
  product_id: number;
  product_name: string;
  quantity: number;
  item_label: string | null;
  also_in: { warehouse_id: number; warehouse_name: string; product_id: number; quantity: number; same_product: boolean }[];
}
type DuplicateState = { loading: boolean; error?: string; items?: DuplicateItem[] };

function StockOverviewTab({ onGoToTab }: { onGoToTab: (tab: Tab) => void }) {
  const canFindDuplicates = usePermission("discord_sync.duplicates");
  const [dupResults, setDupResults] = useState<Record<number, DuplicateState>>({});
  const [deletingDup, setDeletingDup] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseStockOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [unmappedCount, setUnmappedCount] = useState(0);
  const [unmappedItems, setUnmappedItems] = useState<UnmappedItemGroup[]>([]);
  const [showSyncReminder, setShowSyncReminder] = useState(() => !hasSyncedThisSession());
  const [clearingWarehouseId, setClearingWarehouseId] = useState<number | null>(null);
  // Ein eigenes Suchfeld je Lager (Schlüssel = Lager-ID) - filtert nur die Anzeige.
  const [searches, setSearches] = useState<Record<number, string>>({});

  const loadStatus = useCallback(async () => {
    const result = await api.get<DiscordSyncStatus>("/api/warehouse-sync/status");
    setUnmappedCount(result.unmappedCount);
  }, []);

  const loadUnmappedItems = useCallback(async () => {
    const result = await api.get<UnmappedItemGroup[]>("/api/warehouse-sync/unmapped-items");
    setUnmappedItems(result);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [result] = await Promise.all([
        api.get<WarehouseStockOverview[]>("/api/warehouse-sync/stock-overview"),
        loadStatus(),
        loadUnmappedItems(),
      ]);
      setWarehouses(result);
    } finally {
      setLoading(false);
    }
  }, [loadStatus, loadUnmappedItems]);

  useEffect(() => { load(); }, [load]);

  // "Nein" wird serverseitig gespeichert (siehe UnmappedItemRow) - die
  // Meldung verschwindet dadurch dauerhaft (nicht nur clientseitig/bis zum
  // nächsten Tab-Wechsel), load() holt danach einfach den aktuellen Stand.
  function handleDismissed() {
    load();
  }

  function handleResolved() {
    load();
  }

  async function handleSync() {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await api.post<{ processed: number }>("/api/warehouse-sync/sync-now");
      setSyncMessage({
        tone: "success",
        text: result.processed > 0
          ? `Synchronisierung abgeschlossen: ${result.processed} neue Meldung(en) verarbeitet.`
          : "Synchronisierung abgeschlossen: keine neuen Meldungen im Discord-Channel.",
      });
      markSyncedThisSession();
      setShowSyncReminder(false);
      load();
    } catch (err) {
      setSyncMessage({ tone: "danger", text: err instanceof ApiError ? err.message : "Synchronisierung fehlgeschlagen." });
      loadStatus();
    } finally {
      setSyncing(false);
    }
  }

  async function findDuplicates(warehouseId: number) {
    setDupResults((r) => ({ ...r, [warehouseId]: { loading: true } }));
    try {
      const result = await api.get<{ duplicates: DuplicateItem[] }>(`/api/warehouse-sync/warehouses/${warehouseId}/duplicates`);
      setDupResults((r) => ({ ...r, [warehouseId]: { loading: false, items: result.duplicates } }));
    } catch (err) {
      setDupResults((r) => ({ ...r, [warehouseId]: { loading: false, error: err instanceof ApiError ? err.message : "Suche fehlgeschlagen." } }));
    }
  }

  async function deleteDuplicate(w: WarehouseStockOverview, item: DuplicateItem) {
    const others = item.also_in.map((o) => o.warehouse_name).join(", ");
    if (!confirm(
      `"${item.product_name}" (${item.quantity} Stk.) wirklich aus "${w.name}" löschen?

Der Artikel bleibt in: ${others}.
Der Bestand in "${w.name}" wird entfernt. Gehört der Artikel nur zu diesem Lager, wird er samt Discord-Zuordnung vollständig gelöscht. Das kann nicht rückgängig gemacht werden.`
    )) return;
    const key = `${w.id}:${item.product_id}`;
    setDeletingDup(key);
    try {
      await api.delete(`/api/warehouse-sync/articles/${item.product_id}?warehouseId=${w.id}`);
      await Promise.all([load(), findDuplicates(w.id)]);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeletingDup(null);
    }
  }

  // Zwei-stufige Bestätigung wie gefordert: die native confirm()-Warnung
  // muss explizit bestätigt werden, bevor überhaupt ein API-Aufruf erfolgt -
  // derselbe, bereits an anderer Stelle in diesem Modul verwendete Ansatz
  // (z.B. Löschen einer Zuordnung).
  async function handleClearWarehouse(w: WarehouseStockOverview) {
    if (!confirm(
      `Lager "${w.name}" wirklich vollständig leeren?\n\nALLE Artikel in diesem Lager (${w.items.length}) werden entfernt (nicht nur der Bestand auf 0 gesetzt) - existiert ein Artikel in keinem weiteren Lager mehr, wird er vollständig gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`
    )) {
      return;
    }
    setClearingWarehouseId(w.id);
    try {
      await api.post<{ clearedArticles: number; totalRemoved: number }>(`/api/warehouse-sync/warehouses/${w.id}/empty`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Leeren fehlgeschlagen.");
    } finally {
      setClearingWarehouseId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">Aktueller Bestand aller Lagerstandorte.</p>
        <div className="flex items-center gap-2">
          {showSyncReminder && (
            <span className="sync-reminder-arrow" title="Bitte einmal synchronisieren, um aktuelle Discord-Meldungen zu übernehmen.">➜</span>
          )}
          <button className="btn-primary whitespace-nowrap" onClick={handleSync} disabled={syncing}>
            {syncing ? "Synchronisiere…" : "🔄 Synchronisieren"}
          </button>
        </div>
      </div>

      {syncMessage && (
        <div className={`text-sm rounded-lg px-3 py-2 ${syncMessage.tone === "success" ? "bg-success-muted text-success" : "bg-danger-muted text-danger"}`}>
          {syncMessage.text}
        </div>
      )}

      {unmappedItems.length > 0 ? (
        <Card className="p-4 border border-warning/50">
          <p className="text-sm text-warning font-medium mb-3">
            ⚠️ {unmappedItems.length === 1 ? "1 unbekannter Artikel" : `${unmappedItems.length} unbekannte Artikel`} erkannt - Discord-Item-Name und Lager wurden bereits gelesen. Als neuer Artikel übernehmen?
          </p>
          <div className="space-y-2">
            {unmappedItems.map((g) => (
              <UnmappedItemRow key={groupKey(g)} group={g} onDismiss={handleDismissed} onResolved={handleResolved} />
            ))}
          </div>
          {unmappedCount > unmappedItems.reduce((sum, g) => sum + g.occurrences, 0) && (
            <button className="text-xs text-gray-500 hover:text-gray-300 hover:underline mt-3" onClick={() => onGoToTab("log")}>
              Weitere unklare Meldungen (z.B. unbekannte Lagerschlüssel) im Sync-Protokoll ansehen
            </button>
          )}
        </Card>
      ) : unmappedCount > 0 && (
        <div className="flex items-center justify-between gap-3 bg-warning-muted text-warning text-sm rounded-lg px-3 py-2">
          <span>⚠️ {unmappedCount} unklare Meldung(en) (z.B. unbekannte Lagerschlüssel) erkannt - bitte zuordnen.</span>
          <button className="btn-secondary px-3 py-1 text-xs whitespace-nowrap" onClick={() => onGoToTab("log")}>Zum Sync-Protokoll</button>
        </div>
      )}

      {loading ? <Spinner /> : warehouses.length === 0 ? (
        <EmptyState message="Keine Lagerstandorte vorhanden." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {warehouses.map((w) => {
            const search = (searches[w.id] ?? "").trim().toLowerCase();
            const visibleItems = search ? w.items.filter((i) => i.product_name.toLowerCase().includes(search)) : w.items;
            return (
            <Card key={w.id} padded={false} className="overflow-hidden">
              <div className="px-5 py-4 border-b border-surface-border flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-display text-base font-semibold text-gray-100 uppercase tracking-wide">{w.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {search ? `${visibleItems.length} von ${w.items.length} Artikeln` : `${w.items.length} Artikel`}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  {canFindDuplicates && (
                    <button
                      className="btn-secondary px-2.5 py-1 text-xs whitespace-nowrap"
                      disabled={dupResults[w.id]?.loading || w.items.length === 0}
                      onClick={() => findDuplicates(w.id)}
                    >
                      {dupResults[w.id]?.loading ? "Suche…" : "Doppelte suchen"}
                    </button>
                  )}
                  <button
                    className="btn-danger px-2.5 py-1 text-xs whitespace-nowrap"
                    disabled={clearingWarehouseId !== null || w.items.length === 0}
                    onClick={() => handleClearWarehouse(w)}
                  >
                    {clearingWarehouseId === w.id ? "Leere…" : "Lager leeren"}
                  </button>
                </div>
              </div>
              {canFindDuplicates && dupResults[w.id] && !dupResults[w.id]!.loading && (
                <div className="px-5 py-3 border-b border-surface-border bg-surface-raised/40 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-gray-300">
                      {dupResults[w.id]!.error
                        ? <span className="text-danger">{dupResults[w.id]!.error}</span>
                        : dupResults[w.id]!.items!.length === 0
                          ? "Keine Doppelten gefunden - kein Artikel dieses Lagers existiert auch in einem anderen Lager."
                          : `${dupResults[w.id]!.items!.length} Artikel auch in anderen Lagern gefunden:`}
                    </p>
                    <button className="text-xs text-gray-500 hover:text-gray-300 shrink-0" onClick={() => setDupResults((r) => { const n = { ...r }; delete n[w.id]; return n; })}>Schließen ✕</button>
                  </div>
                  {(dupResults[w.id]!.items ?? []).map((d) => (
                    <div key={d.product_id} className="bg-surface-card rounded-lg px-3 py-2.5 space-y-2">
                      <p className="text-sm text-gray-100 font-semibold break-words">{d.product_name}</p>
                      <ul className="space-y-1">
                        <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                          <span className="whitespace-nowrap"><Badge tone="accent">{w.name}</Badge></span>
                          <span className="text-gray-100 font-semibold whitespace-nowrap">{d.quantity} Stk.</span>
                          <span className="text-gray-500">hier</span>
                        </li>
                        {d.also_in.map((o) => (
                          <li key={`${o.warehouse_id}:${o.product_id}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                            <span className="whitespace-nowrap"><Badge tone="warning">{o.warehouse_name}</Badge></span>
                            <span className="text-gray-100 font-semibold whitespace-nowrap">{o.quantity} Stk.</span>
                            <span className="text-gray-500">{o.same_product ? "selber Artikel" : "eigener Artikel, gleicher Name"}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        className="btn-danger px-2.5 py-1 text-xs whitespace-nowrap"
                        disabled={deletingDup !== null}
                        onClick={() => deleteDuplicate(w, d)}
                      >
                        {deletingDup === `${w.id}:${d.product_id}` ? "Lösche…" : `Aus ${w.name} löschen`}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="px-5 py-3 border-b border-surface-border">
                <input
                  className="input py-1.5 text-sm"
                  type="search"
                  placeholder={`In ${w.name} suchen…`}
                  value={searches[w.id] ?? ""}
                  onChange={(e) => setSearches((s) => ({ ...s, [w.id]: e.target.value }))}
                />
              </div>
              {w.items.length === 0 ? (
                <EmptyState message="Keine Artikel in diesem Lager." />
              ) : visibleItems.length === 0 ? (
                <EmptyState message="Keine passenden Artikel gefunden." />
              ) : (
                <table className="table-base">
                  <thead><tr><th>Artikel</th><th>Bestand</th></tr></thead>
                  <tbody>
                    {visibleItems.map((i) => (
                      <tr key={i.product_id}>
                        <td className="text-gray-200">{i.product_name}</td>
                        <td className="text-gray-100 font-medium whitespace-nowrap">{i.quantity} Stk.</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Eine Zeile je unbekanntem Artikel: "Ja" fragt die Menge ab und übernimmt
// den Artikel unter EXAKT dem in Discord gelesenen Namen in das bereits
// korrekt erkannte Lager (POST /item-mappings mit productName = itemLabel
// erledigt Zuordnung + Artikel-Anlage + Startbestand in einem Aufruf,
// dieselbe Logik wie in "Artikel-Zuordnung"). "Nein" markiert die
// zugrundeliegenden Ereignisse serverseitig als erledigt (POST
// /unmapped-items/dismiss) - die Meldung bleibt daher dauerhaft ausgeblendet,
// auch nach Tab-Wechsel/Neuladen; eine künftige NEUE Meldung mit demselben,
// weiterhin unzugeordneten Item-Namen taucht bewusst wieder auf.
function UnmappedItemRow({ group, onDismiss, onResolved }: {
  group: UnmappedItemGroup; onDismiss: () => void; onResolved: () => void;
}) {
  const [deciding, setDeciding] = useState(false);
  const [quantity, setQuantity] = useState(String(group.quantity));
  const [submitting, setSubmitting] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    const parsedQuantity = Number(quantity);
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 0) {
      setError("Bitte eine gültige, nicht-negative Ganzzahl eingeben.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/warehouse-sync/item-mappings", {
        itemLabel: group.item_label,
        productName: group.item_label,
        warehouseId: group.warehouse_id,
        initialQuantity: parsedQuantity,
      });
      onResolved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Übernehmen fehlgeschlagen.");
      setSubmitting(false);
    }
  }

  // Wird serverseitig gespeichert (Ereignisse auf "ignored" gesetzt) -
  // andernfalls würde die Meldung nach jedem Tab-Wechsel/Neuladen der Seite
  // wieder auftauchen, da sie sonst nur clientseitig ausgeblendet wäre.
  async function handleDismiss() {
    setError(null);
    setDismissing(true);
    try {
      await api.post("/api/warehouse-sync/unmapped-items/dismiss", {
        itemLabel: group.item_label,
        warehouseId: group.warehouse_id,
      });
      onDismiss();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ausblenden fehlgeschlagen.");
      setDismissing(false);
    }
  }

  return (
    <div className="bg-surface-raised rounded-lg px-3 py-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm text-gray-200">
          „<span className="font-medium text-gray-100">{group.item_label}</span>“ in <Badge tone="accent">{group.warehouse_name}</Badge>
          {group.occurrences > 1 && <span className="text-xs text-gray-500 ml-2">({group.occurrences}× erkannt)</span>}
        </span>
        {!deciding && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Als neuer Artikel „{group.item_label}“ übernehmen?</span>
            <button className="btn-secondary px-3 py-1 text-xs" disabled={dismissing} onClick={() => setDeciding(true)}>Ja</button>
            <button className="text-xs text-gray-500 hover:text-gray-300 hover:underline px-1 disabled:opacity-40" disabled={dismissing} onClick={handleDismiss}>
              {dismissing ? "…" : "Nein"}
            </button>
          </div>
        )}
      </div>
      {deciding && (
        <div className="flex items-center gap-2 flex-wrap mt-2 pt-2 border-t border-surface-border">
          <label className="text-xs text-gray-500">Menge in „{group.warehouse_name}“:</label>
          <input
            type="number"
            min={0}
            className="input w-24 py-1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            autoFocus
          />
          <button className="btn-primary px-3 py-1 text-xs" disabled={submitting} onClick={handleConfirm}>
            {submitting ? "Übernehme…" : "Übernehmen"}
          </button>
          <button className="btn-secondary px-3 py-1 text-xs" disabled={submitting} onClick={() => { setDeciding(false); setError(null); }}>
            Abbrechen
          </button>
          {error && <span className="text-xs text-danger w-full">{error}</span>}
        </div>
      )}
      {!deciding && error && <p className="text-xs text-danger mt-1">{error}</p>}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Einlagerungen / Auslagerungen: "wer hat was gebucht" - jeweils die
// erfolgreich gebuchten (status="applied") Ereignisse einer Richtung.
// ----------------------------------------------------------------------------
function DirectionLogTab({ direction, title, emptyMessage }: { direction: "in" | "out"; title: string; emptyMessage: string }) {
  const [events, setEvents] = useState<DiscordSyncEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [playerFilter, setPlayerFilter] = useState("");
  const [itemFilter, setItemFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const pageSize = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize), status: "applied", direction });
      if (playerFilter) params.set("player", playerFilter);
      if (itemFilter) params.set("item", itemFilter);
      const result = await api.get<PaginatedResult<DiscordSyncEvent>>(`/api/warehouse-sync/events?${params}`);
      setEvents(result.items);
      setTotal(result.total);
    } finally {
      setLoading(false);
    }
  }, [page, direction, playerFilter, itemFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <CardHeader title={title} subtitle={`${total} gebuchte ${direction === "in" ? "Einlagerung(en)" : "Auslagerung(en)"} über die Discord-Synchronisierung.`} />

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            className="input sm:max-w-xs"
            placeholder="Suche nach Spieler…"
            value={playerFilter}
            onChange={(e) => { setPage(1); setPlayerFilter(e.target.value); }}
          />
          <input
            className="input sm:max-w-xs"
            placeholder="Suche nach Item…"
            value={itemFilter}
            onChange={(e) => { setPage(1); setItemFilter(e.target.value); }}
          />
        </div>
      </Card>

      <Card padded={false} className="overflow-x-auto">
        {loading ? <Spinner /> : events.length === 0 ? (
          <EmptyState message={emptyMessage} />
        ) : (
          <table className="table-base">
            <thead><tr><th>Zeitpunkt</th><th>Spieler</th><th>Item</th><th>Menge</th><th>Lager</th>{direction === "out" && <th>Notiz</th>}</tr></thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="text-gray-500 text-xs whitespace-nowrap">{formatDateTime(e.created_at)}</td>
                  <td className="text-gray-200 font-medium whitespace-nowrap">
                    {e.player_name ?? "—"}{e.player_game_id && <span className="text-gray-500"> (ID: {e.player_game_id})</span>}
                  </td>
                  <td className="text-gray-300">{e.product_name ?? e.item_label ?? "—"}</td>
                  <td className="text-gray-300">{e.quantity ?? "—"}</td>
                  <td><Badge tone="accent">{e.warehouse_name ?? e.storage_key ?? "—"}</Badge></td>
                  {direction === "out" && <td className="min-w-[220px]"><EventNoteField eventId={e.id} initialNote={e.note} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button className="btn-secondary px-3 py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Zurück</button>
          <span className="text-gray-400">Seite {page} von {totalPages}</span>
          <button className="btn-secondary px-3 py-1" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Weiter</button>
        </div>
      )}
    </div>
  );
}

// Leeres Freitextfeld je Auslagerung (Migration 0065) - gespeichert wird beim
// Verlassen des Felds bzw. mit Enter, aber nur, wenn sich der Text geändert hat.
function EventNoteField({ eventId, initialNote }: { eventId: number; initialNote: string | null }) {
  const [value, setValue] = useState(initialNote ?? "");
  const [saved, setSaved] = useState(initialNote ?? "");
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  async function save() {
    if (value.trim() === saved.trim()) return;
    setState("saving");
    try {
      await api.put(`/api/warehouse-sync/events/${eventId}/note`, { note: value });
      setSaved(value);
      setState("saved");
      setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (err) {
      setState("error");
      alert(err instanceof ApiError ? err.message : "Notiz konnte nicht gespeichert werden.");
    }
  }

  return (
    <div className="flex items-center gap-2">
      <input
        className="input py-1 text-sm"
        value={value}
        maxLength={500}
        placeholder="Notiz…"
        onChange={(e) => { setValue(e.target.value); if (state !== "saving") setState("idle"); }}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
      />
      <span className="text-[11px] w-12 shrink-0 text-gray-500">
        {state === "saving" ? "…" : state === "saved" ? <span className="text-success">✓</span> : state === "error" ? <span className="text-danger">Fehler</span> : ""}
      </span>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Bestandskorrektur: einmalige Startbestand-Erfassung je Lager/Artikel,
// damit die automatische Synchronisierung ab einem realistischen Startwert
// fortbucht. Setzt jeweils einen absoluten Bestand (wie eine Inventur).
// ----------------------------------------------------------------------------
type BaselineItem = { product_id: number; product_name: string; quantity: number; item_label: string | null };

function StockBaselineTab() {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState<number | null>(null);
  const [items, setItems] = useState<BaselineItem[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [values, setValues] = useState<Record<number, string>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [editingItem, setEditingItem] = useState<BaselineItem | null>(null);

  const reloadItemsForWarehouse = useCallback(async (id: number) => {
    const overview = await api.get<WarehouseStockOverview[]>("/api/warehouse-sync/stock-overview");
    setItems(overview.find((w) => w.id === id)?.items ?? []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ws, overview] = await Promise.all([
        api.get<Warehouse[]>("/api/warehouse-sync/warehouses-list"),
        api.get<WarehouseStockOverview[]>("/api/warehouse-sync/stock-overview"),
      ]);
      setWarehouses(ws);
      const effectiveId = warehouseId ?? ws[0]?.id ?? null;
      setWarehouseId(effectiveId);
      setItems(overview.find((w) => w.id === effectiveId)?.items ?? []);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // Artikelliste für das gewählte Lager neu laden, wenn sich die Auswahl ändert.
  useEffect(() => {
    if (warehouseId === null) return;
    reloadItemsForWarehouse(warehouseId);
  }, [warehouseId, reloadItemsForWarehouse]);

  async function handleSave(productId: number) {
    if (warehouseId === null) return;
    const raw = values[productId];
    if (raw === undefined || raw === "") return;
    const quantity = Number(raw);
    if (!Number.isInteger(quantity) || quantity < 0) {
      alert("Bitte eine gültige, nicht-negative Ganzzahl eingeben.");
      return;
    }
    setSavingId(productId);
    try {
      await api.post("/api/warehouse-sync/stock", { productId, warehouseId, quantity });
      setItems((list) => list.map((i) => (i.product_id === productId ? { ...i, quantity } : i)));
      setValues((v) => { const next = { ...v }; delete next[productId]; return next; });
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingId(null);
    }
  }

  async function handleDelete(item: BaselineItem) {
    if (warehouseId === null) return;
    if (!confirm(`Artikel "${item.product_name}" wirklich aus "${selectedWarehouseName}" entfernen?`)) return;
    setDeletingId(item.product_id);
    try {
      await api.delete(`/api/warehouse-sync/articles/${item.product_id}?warehouseId=${warehouseId}`);
      setItems((list) => list.filter((i) => i.product_id !== item.product_id));
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  }

  const filtered = items.filter((i) => !search || i.product_name.toLowerCase().includes(search.toLowerCase()));
  const selectedWarehouseName = warehouses.find((w) => w.id === warehouseId)?.name ?? "";

  return (
    <div className="space-y-4">
      <div className="bg-warning-muted text-warning text-sm rounded-lg px-3 py-2">
        Hier wird der Bestand direkt auf den eingegebenen Wert gesetzt (wie eine Inventur) — ideal für die einmalige Ersteinrichtung, damit die automatische Synchronisierung ab einem realistischen Startbestand fortbucht. Neue Artikel, die es im gewählten Lager noch nicht gibt, lassen sich über „+ Neuer Artikel“ anlegen; bestehende Artikel über „Bearbeiten“/„Löschen“ verwalten.
      </div>

      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <select
            className="input sm:max-w-xs"
            value={warehouseId ?? ""}
            onChange={(e) => setWarehouseId(Number(e.target.value))}
          >
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <input
            className="input sm:max-w-xs"
            placeholder="Suche nach Artikel…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className="btn-primary sm:ml-auto whitespace-nowrap"
            disabled={warehouseId === null}
            onClick={() => setShowCreate(true)}
          >
            + Neuer Artikel
          </button>
        </div>
      </Card>

      <Card padded={false} className="overflow-x-auto">
        {loading ? <Spinner /> : filtered.length === 0 ? (
          <EmptyState message={`Noch keine Artikel im Lager „${selectedWarehouseName}“. Über „+ Neuer Artikel“ anlegen.`} />
        ) : (
          <table className="table-base">
            <thead><tr><th>Artikel</th><th>Discord-Item-Name</th><th>Aktueller Bestand</th><th>Neuer Bestand</th><th /></tr></thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.product_id}>
                  <td className="text-gray-200">{i.product_name}</td>
                  <td className="text-gray-400">{i.item_label ?? <span className="text-gray-600 italic">keiner</span>}</td>
                  <td className="text-gray-400">{i.quantity} Stk.</td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      className="input w-28 py-1"
                      placeholder={String(i.quantity)}
                      value={values[i.product_id] ?? ""}
                      onChange={(e) => setValues((v) => ({ ...v, [i.product_id]: e.target.value }))}
                    />
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button
                      className="btn-secondary px-3 py-1 text-xs mr-2"
                      disabled={savingId === i.product_id || values[i.product_id] === undefined || values[i.product_id] === ""}
                      onClick={() => handleSave(i.product_id)}
                    >
                      {savingId === i.product_id ? "Speichern…" : "Setzen"}
                    </button>
                    <button className="text-accent text-xs hover:underline mr-3" onClick={() => setEditingItem(i)}>Bearbeiten</button>
                    <button
                      className="text-danger text-xs hover:underline"
                      disabled={deletingId === i.product_id}
                      onClick={() => handleDelete(i)}
                    >
                      {deletingId === i.product_id ? "Löschen…" : "Löschen"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showCreate && warehouseId !== null && (
        <NewArticleForm
          warehouseId={warehouseId}
          warehouseName={selectedWarehouseName}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); reloadItemsForWarehouse(warehouseId); }}
        />
      )}

      {editingItem && warehouseId !== null && (
        <EditArticleForm
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={() => { setEditingItem(null); reloadItemsForWarehouse(warehouseId); }}
        />
      )}
    </div>
  );
}

function NewArticleForm({ warehouseId, warehouseName, onClose, onCreated }: {
  warehouseId: number; warehouseName: string; onClose: () => void; onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [itemLabel, setItemLabel] = useState("");
  const [itemLabelTouched, setItemLabelTouched] = useState(false);
  const [initialQuantity, setInitialQuantity] = useState("0");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/warehouse-sync/articles", {
        name: name.trim(),
        warehouseId,
        itemLabel: itemLabel.trim(),
        initialQuantity: initialQuantity === "" ? 0 : Number(initialQuantity),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Anlegen fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Neuer Artikel — ${warehouseName}`}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
        <button className="btn-primary" form="new-article-form" disabled={submitting || !name.trim() || !itemLabel.trim()}>
          {submitting ? "Anlegen…" : "Anlegen"}
        </button>
      </>}
    >
      <form id="new-article-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Artikelname *</label>
          <input
            className="input"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!itemLabelTouched) setItemLabel(e.target.value);
            }}
            placeholder="z.B. Eisenerz"
            required
          />
        </div>
        <div>
          <label className="label">Discord-Item-Name *</label>
          <input
            className="input"
            value={itemLabel}
            onChange={(e) => { setItemLabel(e.target.value); setItemLabelTouched(true); }}
            placeholder="Exakter Name im Item-Feld der Discord-Meldung"
            required
          />
          <p className="text-xs text-gray-500 mt-1">Muss exakt dem Text im „Item“-Feld der Discord-Meldung entsprechen (ohne Mengenangabe).</p>
        </div>
        <div>
          <label className="label">Startbestand in „{warehouseName}“</label>
          <input
            type="number"
            min={0}
            className="input w-32"
            value={initialQuantity}
            onChange={(e) => setInitialQuantity(e.target.value)}
          />
        </div>
      </form>
    </Modal>
  );
}

function EditArticleForm({ item, onClose, onSaved }: {
  item: BaselineItem; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(item.product_name);
  const [itemLabel, setItemLabel] = useState(item.item_label ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.put(`/api/warehouse-sync/articles/${item.product_id}`, {
        name: name.trim(),
        itemLabel: itemLabel.trim(),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Artikel bearbeiten"
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
        <button className="btn-primary" form="edit-article-form" disabled={submitting || !name.trim() || !itemLabel.trim()}>
          {submitting ? "Speichern…" : "Speichern"}
        </button>
      </>}
    >
      <form id="edit-article-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Artikelname *</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label">Discord-Item-Name *</label>
          <input
            className="input"
            value={itemLabel}
            onChange={(e) => setItemLabel(e.target.value)}
            placeholder="Exakter Name im Item-Feld der Discord-Meldung"
            required
          />
          <p className="text-xs text-gray-500 mt-1">Muss exakt dem Text im „Item“-Feld der Discord-Meldung entsprechen (ohne Mengenangabe).</p>
        </div>
      </form>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// Lager-Zuordnung
// ----------------------------------------------------------------------------
function StorageMappingTab() {
  const [mappings, setMappings] = useState<DiscordStorageMapping[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<DiscordStorageMapping | null | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, w] = await Promise.all([
        api.get<DiscordStorageMapping[]>("/api/warehouse-sync/storage-mappings"),
        api.get<Warehouse[]>("/api/warehouse-sync/warehouses-list"),
      ]);
      setMappings(m);
      setWarehouses(w);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(m: DiscordStorageMapping) {
    if (!confirm(`Zuordnung "${m.storage_key}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/warehouse-sync/storage-mappings/${m.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Ordnet die im Spiel verwendeten Lagerschlüssel den bestehenden Lagerstandorten zu.</p>
        <button className="btn-primary whitespace-nowrap" onClick={() => setEditing(null)}>+ Neue Zuordnung</button>
      </div>

      <Card padded={false} className="overflow-x-auto">
        {loading ? <Spinner /> : mappings.length === 0 ? (
          <EmptyState message="Keine Lager-Zuordnungen vorhanden." />
        ) : (
          <table className="table-base">
            <thead><tr><th>Discord-Lagerschlüssel</th><th>Lagerstandort</th><th /></tr></thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.id}>
                  <td className="font-mono text-gray-200">{m.storage_key}</td>
                  <td><Badge tone="accent">{m.warehouse_name}</Badge></td>
                  <td className="text-right whitespace-nowrap">
                    <button className="text-accent text-xs hover:underline mr-3" onClick={() => setEditing(m)}>Bearbeiten</button>
                    <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(m)}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {editing !== undefined && (
        <StorageMappingForm
          mapping={editing}
          warehouses={warehouses}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }}
        />
      )}
    </div>
  );
}

function StorageMappingForm({ mapping, warehouses, onClose, onSaved }: {
  mapping: DiscordStorageMapping | null; warehouses: Warehouse[]; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!mapping;
  const [storageKey, setStorageKey] = useState(mapping?.storage_key ?? "");
  const [warehouseId, setWarehouseId] = useState(mapping?.warehouse_id ? String(mapping.warehouse_id) : "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = { storageKey: storageKey.trim(), warehouseId: Number(warehouseId) };
      if (isEdit) await api.put(`/api/warehouse-sync/storage-mappings/${mapping!.id}`, payload);
      else await api.post("/api/warehouse-sync/storage-mappings", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? "Lager-Zuordnung bearbeiten" : "Neue Lager-Zuordnung"}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
        <button className="btn-primary" form="storage-mapping-form" disabled={submitting || !warehouseId}>{submitting ? "Speichern…" : "Speichern"}</button>
      </>}
    >
      <form id="storage-mapping-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Discord-Lagerschlüssel *</label>
          <input className="input font-mono" value={storageKey} onChange={(e) => setStorageKey(e.target.value)} placeholder="z.B. blackreaper_chef_storage" required />
        </div>
        <div>
          <label className="label">Lagerstandort *</label>
          <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
            <option value="">— auswählen —</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
      </form>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// Sync-Protokoll: vollständiges Protokoll ALLER erkannten Nachrichten,
// inklusive nicht zuordenbarer und ignorierter - im Unterschied zu den
// kuratierten "Einlagerungen"/"Auslagerungen"-Ansichten (nur erfolgreich
// gebuchte) dient dies primär der Fehlersuche/Einrichtung.
// ----------------------------------------------------------------------------
function SyncLogTab() {
  const [events, setEvents] = useState<DiscordSyncEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [playerFilter, setPlayerFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const pageSize = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (statusFilter) params.set("status", statusFilter);
      if (playerFilter) params.set("player", playerFilter);
      const result = await api.get<PaginatedResult<DiscordSyncEvent>>(`/api/warehouse-sync/events?${params}`);
      setEvents(result.items);
      setTotal(result.total);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, playerFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            className="input sm:max-w-xs"
            placeholder="Suche nach Spieler…"
            value={playerFilter}
            onChange={(e) => { setPage(1); setPlayerFilter(e.target.value); }}
          />
          <select className="input sm:max-w-[220px]" value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}>
            <option value="">Alle Ereignisse</option>
            <option value="applied">Gebucht</option>
            <option value="unmapped_item">Unbekanntes Item</option>
            <option value="unmapped_storage">Unbekanntes Lager</option>
            <option value="error">Fehler</option>
            <option value="ignored">Ignoriert</option>
          </select>
        </div>
      </Card>

      <Card padded={false} className="overflow-x-auto">
        {loading ? <Spinner /> : events.length === 0 ? (
          <EmptyState message="Noch keine Sync-Ereignisse vorhanden." />
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>Zeitpunkt</th><th>Status</th><th>Richtung</th><th>Item</th><th>Menge</th><th>Lager</th><th>Spieler</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="text-gray-500 text-xs whitespace-nowrap">{formatDateTime(e.created_at)}</td>
                  <td>
                    <Badge tone={EVENT_STATUS_TONE[e.status]}>{EVENT_STATUS_LABELS[e.status]}</Badge>
                    {e.error_message && <span className="block text-[11px] text-danger mt-0.5">{e.error_message}</span>}
                  </td>
                  <td className="text-gray-400">{e.direction === "in" ? "Einlagerung" : e.direction === "out" ? "Auslagerung" : "—"}</td>
                  <td className="text-gray-300">{e.product_name ?? e.item_label ?? "—"}</td>
                  <td className="text-gray-300">{e.quantity ?? "—"}</td>
                  <td className="text-gray-400">{e.warehouse_name ?? e.storage_key ?? "—"}</td>
                  <td className="text-gray-400">{e.player_name ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button className="btn-secondary px-3 py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Zurück</button>
          <span className="text-gray-400">Seite {page} von {totalPages}</span>
          <button className="btn-secondary px-3 py-1" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Weiter</button>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Einstellungen: Status + Discord-Log-Channel (bewusst getrennt von der
// Bestandsübersicht, siehe Anforderung).
// ----------------------------------------------------------------------------
function SettingsTab() {
  const [status, setStatus] = useState<DiscordSyncStatus | null>(null);
  const [channelId, setChannelId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get<DiscordSyncStatus>("/api/warehouse-sync/status");
      setStatus(result);
      setChannelId(result.channelId);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      await api.put("/api/warehouse-sync/channel-id", { channelId: channelId.trim() });
      setSaved(true);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  if (loading || !status) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Status"
          value={status.channelConfigured ? "Aktiv" : "Nicht eingerichtet"}
          icon={<span>{status.channelConfigured ? "✅" : "⚠️"}</span>}
          tone={status.channelConfigured ? "success" : "warning"}
        />
        <StatCard
          label="Letzte Synchronisierung"
          value={status.lastEventAt ? formatDateTime(status.lastEventAt) : "—"}
          icon={<span>🕓</span>}
        />
        <StatCard
          label="Offene Zuordnungen"
          value={String(status.unmappedCount)}
          icon={<span>❓</span>}
          tone={status.unmappedCount > 0 ? "warning" : "default"}
        />
      </div>

      {status.errorCount > 0 && (
        <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">
          {status.errorCount} fehlgeschlagene Verarbeitung(en) im Sync-Protokoll — Details unter „Sync-Protokoll".
        </div>
      )}

      <Card className="p-5">
        <CardHeader
          title="Discord-Log-Channel"
          subtitle="Die Channel-ID des Discord-Channels, in dem das Spiel die Ein-/Auslagerungs-Meldungen postet."
        />
        <form onSubmit={handleSave} className="space-y-3">
          {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
          {saved && <div className="bg-success-muted text-success text-sm rounded-lg px-3 py-2">Gespeichert.</div>}
          <div>
            <label className="label">Channel-ID</label>
            <input
              className="input sm:max-w-sm"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              placeholder="z.B. 123456789012345678"
            />
            <p className="text-xs text-gray-500 mt-1">
              In Discord: Entwicklermodus aktivieren (Einstellungen → Erweitert), dann Rechtsklick auf den Channel → „ID kopieren".
              Zusätzlich muss serverseitig einmalig das Discord-Bot-Token als Secret hinterlegt werden.
            </p>
          </div>
          <button className="btn-primary" disabled={saving}>{saving ? "Speichern…" : "Speichern"}</button>
        </form>
      </Card>
    </div>
  );
}
